import React, { useState, useEffect } from 'react';
import HeaderLogged from './components/header_logged';
import Container from './components/container';
import SelectBox from './components/SelectBox';
import api from './api/client';
import Notification from './components/Notification';

const ScannersInvSeeds = () => {
    // State for first form (Using Scanner)
    const [form1State, setForm1State] = useState({
        seedsID: '',
        bagnumid: '',
        combinedSeedsName: '',
        oldLot: '',
        containerID: 1,
        lot: '',
        currentWeight: '',
        testGermRate: '',
        testGermDate: '',
        isLabelVisible: false
    });

    // State for second form (Using Seeds Name)
    const [form2State, setForm2State] = useState({
        seedsID: '',
        bagnumid: '',
        combinedSeedsName: '',
        containerID: 1,
        lot: '',
        currentWeight: '',
        testGermRate: '',
        testGermDate: '',
        oldLot: '',
        isLabelVisible: false
    });

    const [notification, setNotification] = useState({ message: '', type: '' });
    const [selectedVariant, setSelectedVariant] = useState('scanner'); // Form selection
    const [logData, setLogData] = useState([]); // State for log data
    const [expandedSeeds, setExpandedSeeds] = useState(new Set()); // State for expanded seed rows

    // Errors for bagnumid
    const [form1BagNumError, setForm1BagNumError] = useState('');
    const [form2BagNumError, setForm2BagNumError] = useState('');

    // Validate if bagnumid starts with a letter
    const isValidBagNumID = (bagnumid) => /^[A-Za-z]/.test(bagnumid);

    // Fetch log data on component mount
    useEffect(() => {
        const fetchLogData = async () => {
            try {
                const response = await api.get(`/api/seedslocationwh_log`);
                setLogData(response.data);
            } catch (error) {
                console.error('Error fetching log data:', error);
                setNotification({ message: 'Failed to load history data.', type: 'error' });
            }
        };
        fetchLogData();
    }, []);

    // Group log data by seedsID and combinedSeedsName
    const groupedLogData = logData.reduce((acc, record) => {
        const { seedsID, combinedSeedsName } = record;
        if (!acc[seedsID]) {
            acc[seedsID] = { combinedSeedsName, bags: [] };
        }
        acc[seedsID].bags.push(record);
        return acc;
    }, {});

    // Check if all bags for a seed have matching weights
    const getSeedStatus = (bags) => {
        return bags.every(bag => bag.currentWeight === bag.old_weight || bag.old_weight === null);
    };

    // Toggle expansion of seed row
    const toggleSeedExpansion = (seedsID) => {
        console.log('Toggling seed expansion for:', seedsID); // Debug log
        const newExpanded = new Set(expandedSeeds);
        if (newExpanded.has(seedsID)) {
            newExpanded.delete(seedsID);
        } else {
            newExpanded.add(seedsID);
        }
        setExpandedSeeds(newExpanded);
    };

    // Handle form variant change
    const handleVariantChange = (e) => {
        setSelectedVariant(e.target.value);
        if (e.target.value === 'scanner') {
            setForm2State({
                seedsID: '',
                bagnumid: '',
                containerID: 1,
                combinedSeedsName: '',
                lot: '',
                currentWeight: '',
                testGermRate: '',
                testGermDate: '',
                oldLot: '',
                isLabelVisible: false
            });
        } else {
            setForm1State({
                seedsID: '',
                bagnumid: '',
                combinedSeedsName: '',
                oldLot: '',
                containerID: 1,
                lot: '',
                currentWeight: '',
                testGermRate: '',
                testGermDate: '',
                isLabelVisible: false
            });
        }
    };

    // Handle changes in first form (scanner)
    const handleForm1Change = (e) => {
        const { name, value } = e.target;
        if (name === 'bagnumid') {
            if (!isValidBagNumID(value)) {
                setForm1BagNumError('Invalid Bag Number');
            } else {
                setForm1BagNumError('');
            }
        }
        setForm1State((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    // Handle changes in second form (seedsname)
    const handleForm2Change = (e) => {
        const { name, value } = e.target;
        if (name === 'bagnumid') {
            if (!isValidBagNumID(value)) {
                setForm2BagNumError('Invalid Bag Number');
            } else {
                setForm2BagNumError('');
            }
        }
        setForm2State((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    // Fetch combinedSeedsName
    const fetchCombinedSeedsName = async (seedsID) => {
        try {
            const seedsResponse = await api.get(`/api/combinedSeedsName/${seedsID}`);
            console.log('Combined Seeds Name Response:', seedsResponse.data);
            return seedsResponse.data.combinedSeedsName || '';
        } catch (error) {
            console.error('Error fetching seeds name:', error);
            return '';
        }
    };

    // Fetch oldLot with not found message
    const fetchOldLot = async (seedsID) => {
        try {
            const lotResponse = await api.get(`/api/oldseedslot/${seedsID}`);
            console.log('Lot Response:', lotResponse.data);
            return lotResponse.data.lot || '';
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.log('Lot not found for seedsID:', seedsID);
                return 'Lot record not found';
            } else {
                console.error('Error fetching lot data:', error);
                return '';
            }
        }
    };

    // Handle seedsID change in first form
    const handleForm1SeedsIDChange = async (e) => {
        const seedsID = e.target.value;
        setForm1State((prevState) => ({ ...prevState, seedsID }));

        if (seedsID) {
            const combinedSeedsName = await fetchCombinedSeedsName(seedsID);
            const oldLot = await fetchOldLot(seedsID);
            setForm1State((prevState) => ({
                ...prevState,
                combinedSeedsName,
                oldLot,
                isLabelVisible: oldLot !== ''
            }));
        } else {
            setForm1State((prevState) => ({
                ...prevState,
                combinedSeedsName: '',
                oldLot: '',
                isLabelVisible: false
            }));
        }
    };

    // Submit first form (scanner)
    const handleForm1Submit = async (e) => {
        e.preventDefault();
        const { seedsID, containerID, lot, currentWeight, combinedSeedsName, testGermRate, testGermDate, bagnumid } = form1State;
        const bagNumbers = parseBagNumbers(bagnumid);

        if (bagNumbers.length === 0) {
            setForm1BagNumError('Please enter at least one valid Bag Number.');
            return;
        }

        try {
            for (const bagNumber of bagNumbers) {
                if (!isValidBagNumID(bagNumber)) {
                    setForm1BagNumError(`Invalid Bag Number: ${bagNumber}. It should start with a letter.`);
                    continue;
                }

                const response = await api.get(`/api/seedsinvscanner/${seedsID}/${bagNumber}`);
                if (response.data && response.data.exists) {
                    await api.put(`/api/seedsinvscanner/${seedsID}/${bagNumber}`, {
                        seedsID,
                        bagnumid: bagNumber,
                        containerID,
                        lot,
                        currentWeight,
                        seedsName: combinedSeedsName,
                        germtestrate: testGermRate,
                        germtestdate: testGermDate,
                    });
                } else {
                    await api.post(`/api/seedsinvscanner`, {
                        seedsID,
                        bagnumid: bagNumber,
                        containerID,
                        lot,
                        currentWeight,
                        seedsName: combinedSeedsName,
                        germtestrate: testGermRate,
                        germtestdate: testGermDate,
                    });
                }
            }
            setNotification({ message: 'All records processed successfully!', type: 'success' });
            setForm1State({
                seedsID: '',
                bagnumid: '',
                containerID: 1,
                lot: '',
                currentWeight: '',
                combinedSeedsName: '',
                testGermRate: '',
                testGermDate: '',
                isLabelVisible: false,
            });
            // Refresh log data
            const response = await api.get(`/api/seedslocationwh_log`);
            setLogData(response.data);
        } catch (error) {
            console.error('Error processing records:', error);
            setNotification({ message: 'An error occurred while processing records.', type: 'error' });
        }
    };

    // Fetch combinedSeedsName for second form
    const fetchCombinedSeedsNameForForm2 = async (seedsID) => {
        try {
            const seedsResponse = await api.get(`/api/combinedSeedsName/${seedsID}`);
            console.log('Combined Seeds Name Response (Form 2):', seedsResponse.data);
            return seedsResponse.data.combinedSeedsName || '';
        } catch (error) {
            console.error('Error fetching seeds name for Form 2:', error);
            return '';
        }
    };

    // Fetch oldLot for second form
    const fetchOldLotForForm2 = async (seedsID) => {
        try {
            const lotResponse = await api.get(`/api/oldseedslot/${seedsID}`);
            console.log('Lot Response (Form 2):', lotResponse.data);
            return lotResponse.data.lot || '';
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.log('Lot not found for seedsID (Form 2):', seedsID);
                return 'Lot not found';
            } else {
                console.error('Error fetching lot data (Form 2):', error);
                return '';
            }
        }
    };

    // Handle seed change in second form
    const handleForm2SeedChange = async (value) => {
        setForm2State((prevState) => ({ ...prevState, seedsID: value }));

        if (value) {
            const combinedSeedsName = await fetchCombinedSeedsNameForForm2(value);
            const oldLot = await fetchOldLotForForm2(value);
            setForm2State((prevState) => ({
                ...prevState,
                combinedSeedsName,
                oldLot,
                isLabelVisible: oldLot !== 'Lot not found',
            }));
            if (oldLot === 'Lot not found') {
                setNotification({ message: 'Lot not found for the provided seedsID in Form 2', type: 'error' });
            }
        } else {
            setForm2State((prevState) => ({
                ...prevState,
                combinedSeedsName: '',
                oldLot: '',
                isLabelVisible: false
            }));
        }
    };

    // Submit second form
    const handleForm2Submit = async (e) => {
        e.preventDefault();
        const { seedsID, bagnumid, containerID, lot, currentWeight, combinedSeedsName, testGermRate, testGermDate } = form2State;

        if (!isValidBagNumID(bagnumid)) {
            setForm2BagNumError('Invalid Bag Number. It should start with a letter.');
            return;
        }

        try {
            await api.post(`/api/seedslocationwh`, {
                seedsID,
                bagnumid,
                containerID,
                lot,
                currentWeight,
                seedsName: combinedSeedsName,
                germtestrate: testGermRate,
                germtestdate: testGermDate,
            });
            setNotification({ message: 'Record saved successfully!', type: 'success' });
            // Refresh log data
            const response = await api.get(`/api/seedslocationwh_log`);
            setLogData(response.data);
        } catch (error) {
            console.error('Error saving record (Form 2):', error);
            setNotification({ message: 'An error occurred while saving the record.', type: 'error' });
        }
    };

    const closeNotification = () => {
        setNotification({ message: '', type: '' });
    };

    const parseBagNumbers = (bagnumid) => {
        return bagnumid
            .split(/[\s,]+/)
            .map((item) => item.trim())
            .filter((item, index, self) => item && self.indexOf(item) === index);
    };

    // Format date to USA format (MM/DD/YYYY)
    const formatUSADate = (date) => {
        if (!date || isNaN(new Date(date))) return '-';
        return new Date(date).toLocaleDateString('en-US');
    };

    return (
        <div className="scanners-inv-seeds-page-bg">
            <style>{`
                .inventorylogtable-container {
                    background-color: rgba(255, 255, 255, 0.95);
                    border-radius: 8px;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                    padding: 16px;
                    margin-top: 32px;
                    z-index: 10;
                    position: relative;
                }
                .inventorylogtable-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }
                .inventorylogtable-thead {
                    background-color: #f3f4f6;
                    color: #374151;
                }
                .inventorylogtable-th {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                    font-weight: 600;
                }
                .inventorylogtable-td {
                    padding: 12px;
                    border-bottom: 1px solid #e5e7eb;
                }
                .inventorylogtable-row {
                    cursor: pointer;
                    position: relative;
                    z-index: 11;
                }
                .inventorylogtable-row:hover {
                    background-color: #f9fafb;
                }
                .inventorylogtable-nested-table {
                    width: 100%;
                    background-color: #f9fafb;
                    border-collapse: collapse;
                    margin-top: 8px;
                    margin-bottom: 8px;
                    z-index: 12;
                }
                .inventorylogtable-nested-thead {
                    background-color: #e5e7eb;
                }
                .inventorylogtable-nested-td {
                    padding: 12px;
                    border-bottom: 1px solid #e5e7eb;
                }
                .inventorylogtable-mismatch {
                    background-color: rgba(254, 226, 226, 0.8);
                }
                .inventorylogtable-no-history {
                    text-align: center;
                    padding: 24px;
                    color: #6b7280;
                    font-style: italic;
                }
                .inventorylogtable-expand-icon {
                    margin-right: 8px;
                    display: inline-block;
                    width: 16px;
                }
                .inventorylogtable-nested-container {
                    padding: 8px;
                    background-color: #f9fafb;
                    z-index: 12;
                }
                .inventorylogtable-heading {
                    font-size: 24px;
                    font-weight: 700;
                    margin-bottom: 16px;
                }
            `}</style>
            <Notification
                message={notification.message}
                type={notification.type}
                onClose={closeNotification}
            />
            <HeaderLogged />
            <Container className="scanners-inv-seeds-page">
                <div className="seedsinv-variants-inner">
                    <label className="seedsinv-fromvariant">
                        <input type="radio" value="scanner" name="seedsinvinputvariant" defaultChecked onChange={handleVariantChange} />
                        <span className="custom-radio"></span>
                        Using Scanner
                    </label>
                    <label className="seedsinv-fromvariant">
                        <input type="radio" value="seedsname" name="seedsinvinputvariant" onChange={handleVariantChange} />
                        <span className="custom-radio"></span>
                        Using Seeds Name
                    </label>
                </div>

                {/* Form 1 (Using Scanner) */}
                <div className={`scannerform-inner ${selectedVariant === 'scanner' ? 'active-formvar' : 'hidden-formvar'}`}>
                    <form className="scannerform" onSubmit={handleForm1Submit}>
                        <h2 className="popup__title">Seeds Inventory</h2>
                        <h3 className="popup__subtitle">Please use the scanner to input actual data</h3>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Seeds ID</label>
                            <input className='scannerform__input' name="seedsID" value={form1State.seedsID} onChange={handleForm1SeedsIDChange} placeholder='Please scan the QR-code for seeds' />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Bag Number</label>
                            <textarea className='scannerform__textarea' name="bagnumid" value={form1State.bagnumid} onChange={handleForm1Change} placeholder="Enter bag numbers separated by commas, spaces, or new lines" ></textarea>
                            {form1BagNumError && <span style={{ color: 'red', margin:'5px' }}>{form1BagNumError}</span>}
                        </div>
                        <div className={`scannerform-inputgroup dynamic-label ${form1State.isLabelVisible ? 'visible-label' : 'hidden-label'}`} style={{ maxWidth: '250px' }}>
                            <label className='scannerform-label'>
                                Seeds Name - <span className='scannerform-dynlabel'>{form1State.combinedSeedsName}</span>
                            </label>
                        </div>
                        <div className={`scannerform-inputgroup dynamic-label ${form1State.isLabelVisible ? 'visible-label' : 'hidden-label'}`} style={{ maxWidth: '250px' }}>
                            <label className='scannerform-label'>
                                Lot - <span className='scannerform-dynlabel'>{form1State.oldLot}</span>
                            </label>
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Container Name</label>
                            <SelectBox tableName="containers" displayField="containerName" updateField="containerID" selectedValue={form1State.containerID} onChange={(value) => setForm1State({ ...form1State, containerID: value })} />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Lot</label>
                            <input className='scannerform__input' name="lot" value={form1State.lot} onChange={handleForm1Change} placeholder='Example: C1, C2' />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Current Weight (lb)</label>
                            <input className='scannerform__input' name="currentWeight" value={form1State.currentWeight} onChange={handleForm1Change} placeholder='Example: 5.2' />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Germination Rate</label>
                            <input className='scannerform__input' name="testGermRate" value={form1State.testGermRate} onChange={handleForm1Change} placeholder='Example: 85 (without %)' />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Germination Test Date</label>
                            <input className='scannerform__input' name="testGermDate" type='date' value={form1State.testGermDate} onChange={handleForm1Change} />
                        </div>
                        <button type="submit" className="btn btn__green mt-15">Add Record</button>
                    </form>
                </div>

                {/* Form 2 (Using Seeds Name) */}
                <div className={`scannerform-inner ${selectedVariant === 'seedsname' ? 'active-formvar' : 'hidden-formvar'}`}>
                    <form className="scannerform" onSubmit={handleForm2Submit}>
                        <h2 className="popup__title">Seeds Inventory</h2>
                        <h3 className="popup__subtitle">Please use the scanner to input actual data</h3>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Seeds</label>
                            <SelectBox tableName="seeds" displayField="combinedSeedsName" updateField="seedsID" selectedValue={form2State.seedsID} onChange={handleForm2SeedChange} />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Bag Number</label>
                            <input className='scannerform__input' name="bagnumid" value={form2State.bagnumid} onChange={handleForm2Change} placeholder='Insert the bag number' />
                            {form2BagNumError && <span style={{ color: 'red', margin:'5px' }}>{form2BagNumError}</span>}
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Container Name</label>
                            <SelectBox tableName="containers" displayField="containerName" updateField="containerID" selectedValue={form2State.containerID} onChange={(value) => setForm2State({ ...form2State, containerID: value })} />
                        </div>
                        <div className={`scannerform-inputgroup dynamic-label ${form2State.isLabelVisible ? 'visible-label' : 'hidden-label'}`} style={{ maxWidth: '250px' }}>
                            <label className='scannerform-label'>
                                Lot - <span className='scannerform-dynlabel'>{form2State.oldLot}</span>
                            </label>
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Lot</label>
                            <input className='scannerform__input' name="lot" value={form2State.lot} onChange={handleForm2Change} placeholder='Example: C1, C2' />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Current Weight (lb)</label>
                            <input className='scannerform__input' name="currentWeight" value={form2State.currentWeight} onChange={handleForm2Change} placeholder='Example: 5.2' />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Germination Rate</label>
                            <input className='scannerform__input' name="testGermRate" value={form2State.testGermRate} onChange={handleForm2Change} placeholder='Example: 85 (without %)' />
                        </div>
                        <div className="scannerform-inputgroup">
                            <label className='scannerform-label'>Germination Test Date</label>
                            <input className='scannerform__input' name="testGermDate" type='date' value={form2State.testGermDate} onChange={handleForm2Change} />
                        </div>
                        <button type="submit" className="btn btn__green mt-15">Add Record</button>
                    </form>
                </div>

            </Container>
        </div>
    );
};

export default ScannersInvSeeds;